import { useEffect, useRef, useState } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { usePresence } from "contexts/PresenceContext";

/**
 * Hook to manage page presence tracking
 * Automatically tracks who is viewing the current page
 * @returns {{ viewers: Array, isConnected: boolean }} - List of other users viewing this page
 */
export const usePagePresence = () => {
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  const { currentPagePath, currentEntity } = usePresence();
  const [viewers, setViewers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  // Latest page:join payload (page + entity context). Updated on entity change
  // so a socket reconnect re-joins with the CURRENT entity room, not the one
  // captured when the mount effect first ran (currentEntity is intentionally
  // not a dependency of the join effect below).
  const joinPayloadRef = useRef(null);

  useEffect(() => {
    if (!socket || !currentPagePath || !currentUser) {
      return;
    }

    // Agreed presence contract: viewer objects are { userId, name, imageThumbnail }.
    // Identity is resolved server-side from the verified token; this payload only
    // supplies display data + a fallback key, but we match the contract key so
    // both the fallback and primary paths stay consistent.
    const userData = {
      userId: currentUser.id,
      name: currentUser.Name,
      imageThumbnail: currentUser.ImageThumbnail,
    };

    // Join the page room with optional entity info
    const joinPayload = {
      pagePath: currentPagePath,
      user: userData,
    };

    // Add entity info if available
    if (currentEntity?.entityType && currentEntity?.entityId) {
      joinPayload.entityType = currentEntity.entityType;
      joinPayload.entityId = currentEntity.entityId;
    }

    // Keep the latest payload in a ref: this effect does not re-run on entity
    // change, so a closure-captured payload would go stale and a reconnect
    // would re-join WITHOUT the entity room.
    joinPayloadRef.current = joinPayload;

    // Re-emit page:join whenever the socket (re)connects. The server drops all
    // rooms on disconnect, so a network blip mid-view would otherwise leave us
    // out of the page room AND the entity room until this effect re-runs.
    const emitJoin = () => {
      socket.emit("page:join", joinPayloadRef.current);
      setIsConnected(true);
    };

    // If the socket is already connected when this effect runs (page/entity
    // change while connected), join immediately. If it isn't connected yet,
    // the 'connect' listener below fires on the initial connection AND on
    // every reconnect, so the join is always (re)emitted.
    if (socket.connected) {
      emitJoin();
    }
    socket.on("connect", emitJoin);

    // Listen for presence updates
    const handlePresence = ({
      pagePath: receivedPath,
      viewers: receivedViewers,
    }) => {
      if (receivedPath !== currentPagePath) return;

      // Defensive: never crash on a malformed broadcast.
      const safeViewers = Array.isArray(receivedViewers)
        ? receivedViewers
        : [];

      // Filter out current user from the viewers list.
      // Number-coerce both sides: the server emits userId as a number from the
      // verified token, but currentUser.id may be string/number depending on
      // context — strict !== would let the user's own avatar through.
      const otherViewers = safeViewers.filter(
        (v) => Number(v.userId) !== Number(currentUser.id),
      );
      setViewers(otherViewers);
    };

    socket.on("page:presence", handlePresence);

    // Cleanup: leave the room when page changes or unmounts
    return () => {
      const leavePayload = { pagePath: currentPagePath };

      // Include entity info if available (ref stays in sync with the latest
      // entity context even though this effect doesn't re-run on entity change)
      if (joinPayloadRef.current?.entityType && joinPayloadRef.current?.entityId) {
        leavePayload.entityType = joinPayloadRef.current.entityType;
        leavePayload.entityId = joinPayloadRef.current.entityId;
      }

      socket.emit("page:leave", leavePayload);
      socket.off("page:presence", handlePresence);
      socket.off("connect", emitJoin);
      setIsConnected(false);
      setViewers([]);
    };
    // NOTE: currentEntity is intentionally NOT a dependency here. Entity
    // changes (e.g. which Settings tab is active) are pushed via the separate
    // page:entity effect below — re-running this effect on every entity change
    // caused a page:leave + page:join churn that could drop the socket out of
    // the page room and break presence entirely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, currentPagePath, currentUser]);

  // Entity updates (e.g. Settings tab changes) are pushed as a lightweight
  // page:entity event — the server updates the stored viewer's entity info and
  // re-broadcasts presence WITHOUT the socket leaving/rejoining the page room.
  useEffect(() => {
    if (!socket || !currentPagePath || !currentUser) return;

    const entityType = currentEntity?.entityType || null;
    const entityId = currentEntity?.entityId || null;

    // Sync the reconnect payload with the current entity context so a
    // mid-view reconnect re-joins the entity room — page:join carries the
    // entity info and the server joins both rooms from that single event.
    if (joinPayloadRef.current) {
      joinPayloadRef.current.entityType = entityType;
      joinPayloadRef.current.entityId = entityId;
    }

    socket.emit("page:entity", {
      pagePath: currentPagePath,
      entityType,
      entityId,
    });
  }, [socket, currentPagePath, currentUser, currentEntity]);

  return { viewers, isConnected };
};

// Keep old name for backward compatibility but use new implementation
export const useAssignmentPresence = usePagePresence;
